import {ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal} from "@angular/core";
import {navParam} from "../../../../navigation/navigation-params";
import {Page} from "../../../../navigation/page";
import {NotificationService} from "../../../../notification/notification.service";
import {MenuExtraGroupData, MenuExtraProductData} from '../../../../menu/menu-entity.model';
import {MenuModel} from '../../../../menu/menu.model';
import {OrderModel} from "../../../order.model";
import {OrderItem} from '../../../order';
import {NgClass} from '@angular/common';
import {ModalPageLayoutComponent} from '../../../../page-layout/modal-page-layout/modal-page-layout.component';
import {OrderItemOptionControlComponent} from "../order-item-option-control/order-item-option-control.component";
import {ButtonComponent} from '../../../../buttons/button/button.component';
import {PageFooterControlDirective} from '../../../../page-layout/page-footer-control.directive';

@Component({
    selector: "woa-order-item-extra-sizes-edit-page",
    templateUrl: "./order-item-extra-sizes-edit-page.component.html",
    styleUrl: "./order-item-extra-sizes-edit-page.component.scss",
    providers: [{provide: Page, useExisting: OrderItemExtraSizesEditPageComponent}],
    imports: [
        ButtonComponent,
        ModalPageLayoutComponent,
        NgClass,
        OrderItemOptionControlComponent,
        PageFooterControlDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemExtraSizesEditPageComponent extends Page {
    public menuModel: MenuModel = inject(MenuModel);
    private orderModel: OrderModel = inject(OrderModel);
    public notificationService: NotificationService = inject(NotificationService);

    private readonly orderItem: Signal<OrderItem> = navParam<OrderItem>("orderItem");
    public readonly extra: Signal<MenuExtraGroupData> = navParam<MenuExtraGroupData>("extra");
    public readonly availableExtraProducts: Signal<MenuExtraProductData[]> = navParam<MenuExtraProductData[]>("availableExtraProducts");
    public readonly groupName: Signal<string> = navParam<string>("groupName");
    public readonly selectedExtraProduct: WritableSignal<MenuExtraProductData> = navParam<MenuExtraProductData>("selectedExtraProduct");
    public readonly originalSelectedExtraProduct: Signal<MenuExtraProductData> = signal(this.selectedExtraProduct());

    public readonly anyHaveImages: Signal<boolean> = computed(() =>
        this.availableExtraProducts().some((extraProduct: MenuExtraProductData) => !!this.menuModel.standardItemImage()(extraProduct.menuItem)));

    public readonly extraPrice: Signal<(extra: MenuExtraGroupData, extraProduct: MenuExtraProductData) => number> = computed(() => {
        const basePriceForGroup: (extraId: number) => number = this.orderItem().basePriceForGroup();
        return (extra: MenuExtraGroupData, extraProduct: MenuExtraProductData) => {
            if (OrderModel.SHOW_RELATIVE_PRICING_FOR_EXTRA_SIZES) {
                const value: number = extraProduct.price - basePriceForGroup(extra.id);
                return value !== 0 ? value : undefined;
            }
            return extraProduct.price;
        };
    });

    public readonly extraProductAtLimit: Signal<(extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        const sellableAtLimit: (sellableId: number, qtyToAdd: number, qtyAlreadyOrdered: number, excludeQty?: number) => boolean = this.orderModel.sellableAtLimit();
        const quantity: number = this.orderItem().quantitySignal();
        const sellableQtyOrdered: (sellableId: number) => number = this.orderItem().sellableQtyOrdered();
        return this.orderItem() ? (extraProduct: MenuExtraProductData) =>
            extraProduct !== this.originalSelectedExtraProduct() &&
            sellableAtLimit(extraProduct.menuItem ? extraProduct.menuItem.sellable.sellableId : extraProduct.productLine.sellableId,
                quantity, sellableQtyOrdered(extraProduct.productLine.sellableId)) : () => false;
    });

    public selectExtra(extraProduct: MenuExtraProductData): void {
        if (this.selectedExtraProduct() === extraProduct) {
            return;
        }
        if (this.extraProductAtLimit()(extraProduct)) {
            if (this.menuModel.itemOutOfStock()(extraProduct.menuItem)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
            } else if (this.extraProductAtLimit()(extraProduct)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
            }
            return;
        }
        this.selectedExtraProduct.set(extraProduct);
    }
}
